import React, { useState, useEffect, useCallback } from 'react';
import { useScrollLock } from '../../_utils/useScrollLock';
import ReactDOM from 'react-dom';
import VoucherCard from './VoucherCard';
import './VoucherOnboarding.css';

// VoucherOnboarding — a light, premium "how it works" walkthrough opened from
// the `?` button in the Vouchers header. The designs → vouchers relationship
// trips people up, so this explains the flow in four simple slides and shows
// how a voucher looks on the client side.
//
// Presentation: a centred pop-up modal on web, a slide-up sheet on mobile
// (the responsive split lives in VoucherOnboarding.css). The carousel renders
// only the active slide and animates direction-aware enter transitions.

// Tiny helper — build the minimal voucher shape VoucherCard needs so the
// walkthrough can render real, on-brand cards instead of flat mockups.
const pv = (template, theme, value = 50) => ({
  name: 'Gift Card',
  template,
  theme,
  value,
  promo: null,
});

const TOTAL = 4;

const DesignsStage = ({ salonName, currency }) => (
  <div className="vob-stage vob-stage--designs" aria-hidden>
    <div className="vob-fan">
      <div className="vob-fan-card vob-fan-card--l">
        <VoucherCard voucher={pv('anniversary', 'plum')} aesthetic="abstract" salonName={salonName} currency={currency} showPromo={false} />
      </div>
      <div className="vob-fan-card vob-fan-card--r">
        <VoucherCard voucher={pv('thankyou', 'sage')} aesthetic="editorial" salonName={salonName} currency={currency} showPromo={false} />
      </div>
      <div className="vob-fan-card vob-fan-card--c">
        <VoucherCard voucher={pv('birthday', 'rose')} aesthetic="editorial" salonName={salonName} currency={currency} showPromo={false} />
      </div>
    </div>
  </div>
);

// The key clarifying visual: a stack of designs feeding into one voucher,
// so "designs" vs "vouchers" reads at a glance.
const VouchersStage = ({ salonName, currency }) => (
  <div className="vob-stage vob-stage--vouchers" aria-hidden>
    <div className="vob-relation">
      <div className="vob-relation-col">
        <div className="vob-swatch-stack">
          <span className="vob-swatch vob-swatch--1" />
          <span className="vob-swatch vob-swatch--2" />
          <span className="vob-swatch vob-swatch--3" />
        </div>
        <span className="vob-relation-label">Your designs</span>
      </div>

      <div className="vob-relation-arrow">
        <svg width="34" height="14" viewBox="0 0 34 14" fill="none" aria-hidden>
          <path d="M1 7h30m0 0-5.5-5.5M31 7l-5.5 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="vob-relation-col">
        <div className="vob-voucher-card">
          <VoucherCard voucher={pv('justbecause', 'teal')} aesthetic="abstract" salonName={salonName} currency={currency} showPromo={false} />
        </div>
        <div className="vob-denoms">
          <span className="vob-denom">{currency}25</span>
          <span className="vob-denom">{currency}50</span>
          <span className="vob-denom">{currency}100</span>
        </div>
        <span className="vob-relation-label">A voucher</span>
      </div>
    </div>
  </div>
);

const GlobeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" />
  </svg>
);

const StoreIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 9.5 5.2 4h13.6L20 9.5M4 9.5h16M4 9.5v9.5a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5M4 9.5a2.2 2.2 0 0 0 4 0 2.2 2.2 0 0 0 4 0 2.2 2.2 0 0 0 4 0 2.2 2.2 0 0 0 4 0M9.5 20v-5h5v5" />
  </svg>
);

const SellStage = () => (
  <div className="vob-stage vob-stage--sell" aria-hidden>
    <div className="vob-channels">
      <div className="vob-channel">
        <span className="vob-channel-icon"><GlobeIcon /></span>
        <span className="vob-channel-name">Online</span>
        <span className="vob-channel-sub">Booking page</span>
      </div>
      <div className="vob-channel">
        <span className="vob-channel-icon"><StoreIcon /></span>
        <span className="vob-channel-name">In business</span>
        <span className="vob-channel-sub">At the till</span>
      </div>
    </div>
  </div>
);

// Client-side preview — a phone frame so the salon sees exactly how a bought
// voucher looks to their client, with a balance + redeem cue.
const SpendStage = ({ salonName, currency }) => (
  <div className="vob-stage vob-stage--spend" aria-hidden>
    <div className="vob-phone">
      <span className="vob-phone-bar" />
      <div className="vob-phone-screen">
        <div className="vob-phone-card">
          <VoucherCard voucher={pv('birthday', 'rose')} aesthetic="editorial" salonName={salonName} currency={currency} showPromo={false} />
        </div>
        <div className="vob-phone-meta">
          <span className="vob-phone-balance">{currency}50 balance</span>
          <span className="vob-phone-tag">Active</span>
        </div>
        <span className="vob-phone-cta">Use at checkout</span>
      </div>
    </div>
  </div>
);

const SLIDES = [
  {
    key: 'designs',
    title: 'Choose or create your designs',
    body: 'Designs are the look of your vouchers. Use our ready-made editorial and abstract styles, or build your own. This is what clients see when they browse.',
    Stage: DesignsStage,
  },
  {
    key: 'vouchers',
    title: 'Create your vouchers',
    body: 'A voucher is what clients pay for: its values, price and expiry. Assign designs to vouchers, and let your customers pick their favourites.',
    Stage: VouchersStage,
  },
  {
    key: 'sell',
    title: 'Sell vouchers, online or in business',
    body: 'Add vouchers to your online booking page so clients can buy any time, and sell them at the till during a visit. Every sale shows in your Sales tab.',
    Stage: SellStage,
  },
  {
    key: 'spend',
    title: 'Clients choose a design and spend',
    body: 'Buying online, a client picks the design they love. It lands in their wallet, ready to redeem against a booking, online or in the chair.',
    Stage: SpendStage,
  },
];

const VoucherOnboarding = ({ onClose, salonName = 'Your Salon', currency = '£' }) => {
  const [step, setStep] = useState(0);
  // +1 = moving forward, -1 = back. Drives the enter-animation direction.
  const [dir, setDir] = useState(1);
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => { if (onClose) onClose(); }, 280);
  }, [closing, onClose]);

  const goTo = useCallback((next) => {
    const clamped = Math.max(0, Math.min(TOTAL - 1, next));
    setDir(clamped >= step ? 1 : -1);
    setStep(clamped);
  }, [step]);

  const next = useCallback(() => {
    if (step >= TOTAL - 1) { handleClose(); return; }
    goTo(step + 1);
  }, [step, goTo, handleClose]);

  const back = useCallback(() => goTo(step - 1), [step, goTo]);

  // Shared ref-counted scroll lock: overlapping overlays must not restore
  // each other's locked styles.
  useScrollLock(true);

  // Keyboard nav (Esc closes, arrows page).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [handleClose, next, back]);

  const slide = SLIDES[step];
  const Stage = slide.Stage;
  const isLast = step === TOTAL - 1;
  const closingClass = closing ? ' closing' : '';

  const modal = (
    <div className={'vob-overlay' + closingClass} onClick={handleClose}>
      {/* `voucher-v2` scoping class so the --v2 tokens + VoucherCard styles
          apply even though the modal is portaled outside the page tree. */}
      <div className={'vob-modal voucher-v2' + closingClass} role="dialog" aria-modal="true" aria-label="How vouchers work" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="vob-close" onClick={handleClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M1 1l12 12M13 1 1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>

        <div className="vob-eyebrow">How vouchers work</div>

        {/* Carousel — only the active slide renders; key={step} replays the
            enter animation, data-dir flips it left/right. */}
        <div className="vob-carousel">
          <div className="vob-slide" key={step} data-dir={dir > 0 ? 'fwd' : 'back'}>
            <Stage salonName={salonName} currency={currency} />
            <div className="vob-copy">
              <div className="vob-step-count">Step {step + 1} of {TOTAL}</div>
              <h2 className="vob-title">{slide.title}</h2>
              <p className="vob-body">{slide.body}</p>
            </div>
          </div>
        </div>

        <div className="vob-dots" role="tablist" aria-label="Steps">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              type="button"
              className={'vob-dot' + (i === step ? ' is-active' : '')}
              aria-label={`Go to step ${i + 1}`}
              aria-selected={i === step}
              onClick={() => goTo(i)}
            />
          ))}
        </div>

        <div className="vob-footer">
          {step > 0 ? (
            <button type="button" className="vob-btn vob-btn--ghost" onClick={back}>Back</button>
          ) : (
            <button type="button" className="vob-btn vob-btn--ghost" onClick={handleClose}>Skip</button>
          )}
          <button type="button" className="vob-btn vob-btn--primary" onClick={next}>
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default VoucherOnboarding;
